import { createSelector } from "@reduxjs/toolkit";
import { IAerEstimator, IChartDataItemList, ICiiValues } from "./greenTypes";
import { RootState } from "../../core/store";

const selectgreenSlice = (state: RootState) => state.greenSlice;
const searchSlice = (state: RootState) => state.searchSlice;

export const selectHistoricalPerformanceData = createSelector(
    selectgreenSlice,
    (greenSlice) => {
        if (!greenSlice.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData ||
            !greenSlice.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData.vesselLocationChart ||
            !greenSlice.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData.vesselLocationChart.chartDataItemList ||
            !greenSlice.vesselCiiAlignmentsData?.vesselCiiAlignmentData
        ) return null;

        return combineHistoricalPerformanceData(greenSlice?.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData.ciiValues
            , greenSlice.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData.vesselLocationChart?.chartDataItemList);
    }
);

const combineHistoricalPerformanceData = (ciiValues: ICiiValues[], chartDataItemList: IChartDataItemList[]) => {
    // add the current year and one year more forecast data to the result
    const currentYear = new Date().getFullYear();
    const currentYearData = ciiValues?.find((intens) => intens.year === currentYear);

    // get max year data from result
    const currentYearChartDataItemList = chartDataItemList?.find((data) => data.year === currentYear);
    const vesselCiiAlignmentsDataBiggerThanCurrentYear = ciiValues?.filter((intens) => intens.year > currentYear);

    const ppTrajectoryMinimum = parseFloat(currentYearChartDataItemList!.ppTrajectoryMinimum.toFixed(1));
    const ppTrajectoryStriving = parseFloat(currentYearChartDataItemList!.ppTrajectoryStriving.toFixed(1));

    const chartDataBeforeCurrentYear = chartDataItemList?.filter((data) => data.year < currentYear);

    const result = chartDataBeforeCurrentYear.map((est) => {
        const relatedIntensity = ciiValues?.find((intens) => intens.year === est.year);
        return {
            year: est.year,
            ppTrajectoryMinimum: est.ppTrajectoryMinimum,
            ppTrajectoryStriving: est.ppTrajectoryStriving,
            alignmentMin: relatedIntensity?.rollingAerCgDistE && relatedIntensity?.rollingAerCgDistE.toString() !== "-" && (100 * (relatedIntensity?.rollingAerCgDistE - est.ppTrajectoryMinimum) / est.ppTrajectoryMinimum),
            alignmentStriving: relatedIntensity?.rollingAerCgDistE && relatedIntensity?.rollingAerCgDistE.toString() !== "-" && (100 * (relatedIntensity?.rollingAerCgDistE - est.ppTrajectoryStriving) / est.ppTrajectoryStriving),
            ciiValue: relatedIntensity ? relatedIntensity.currentCii : null,
            aer: relatedIntensity ? relatedIntensity.rollingAerCgDist : null,
            aerWtw: relatedIntensity ? relatedIntensity.rollingAerCgDistE : null,
            ciiScore: relatedIntensity ? relatedIntensity.rollingCiiScore : null,
        } as IAerEstimator;
    });

    if (currentYearData) {
        result.push({
            year: currentYear + "F",
            ciiValue: currentYearData.currentCii,
            ciiScore: currentYearData.rollingCiiScore,
            aer: currentYearData.rollingAerCgDist,
            aerWtw: currentYearData.rollingAerCgDistE,
            ppTrajectoryMinimum: ppTrajectoryMinimum,
            ppTrajectoryStriving: ppTrajectoryStriving,
            alignmentMin: currentYearData?.rollingAerCgDistE && currentYearData?.rollingAerCgDistE.toString() !== "-" && (100 * (currentYearData?.rollingAerCgDistE - ppTrajectoryMinimum) / ppTrajectoryMinimum),
            alignmentStriving: currentYearData?.rollingAerCgDistE && currentYearData?.rollingAerCgDistE.toString() !== "-" && (100 * (currentYearData?.rollingAerCgDistE - ppTrajectoryStriving) / ppTrajectoryStriving),
        } as IAerEstimator);

        if (vesselCiiAlignmentsDataBiggerThanCurrentYear && vesselCiiAlignmentsDataBiggerThanCurrentYear.length > 0) {
            vesselCiiAlignmentsDataBiggerThanCurrentYear.forEach((intens) => {
                const chartDataItem = chartDataItemList?.find((data) => data.year === intens.year);
                if (chartDataItem) {
                    result.push({
                        year: intens.year + "F",
                        ciiValue: intens.currentCii,
                        ciiScore: intens.rollingCiiScore,
                        aer: intens.rollingAerCgDist,
                        aerWtw: intens.rollingAerCgDistE,
                        ppTrajectoryMinimum: chartDataItem.ppTrajectoryMinimum,
                        ppTrajectoryStriving: chartDataItem.ppTrajectoryStriving,
                        alignmentMin: intens.rollingAerCgDistE && intens.rollingAerCgDistE.toString() !== "-" && (100 * (intens.rollingAerCgDistE - chartDataItem.ppTrajectoryMinimum) / chartDataItem.ppTrajectoryMinimum),
                        alignmentStriving: intens.rollingAerCgDistE && intens.rollingAerCgDistE.toString() !== "-" && (100 * (intens.rollingAerCgDistE - chartDataItem.ppTrajectoryStriving) / chartDataItem.ppTrajectoryStriving),
                    } as IAerEstimator);
                }
            });
        }
    }

    // sort result by year ascending
    result.sort((a, b) => {
        const yearA = parseInt(a.year.toString().replace("F", ""));
        const yearB = parseInt(b.year.toString().replace("F", ""));
        return yearA - yearB;
    });

    return result;
};

export const selectCurrentYearVesselCiiAlignmentData = createSelector(
    selectgreenSlice,
    (greenSlice) => {
        const vesselCiiAlignmentData = greenSlice.vesselCiiAlignmentsData?.vesselCiiAlignmentData;
        if (vesselCiiAlignmentData) {
            const maxYear = Math.max(...vesselCiiAlignmentData!.map((data) => data.year));
            const lastYearData = vesselCiiAlignmentData?.find((data) => data.year === maxYear);
            return lastYearData;
        }
        return null;
    }
);

export const selectEnvironmentalCredentialsForCurrentYear = createSelector(
    selectgreenSlice,
    (greenSlice) => {
        const vesselLocationChartData = greenSlice.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsData?.vesselLocationChart;
        const vesselCiiAlignmentData = greenSlice.vesselCiiAlignmentsData?.vesselCiiAlignmentData;

        if (vesselLocationChartData && vesselLocationChartData.chartDataItemList && vesselLocationChartData.chartDataItemList.length > 0 && vesselCiiAlignmentData && vesselCiiAlignmentData.length > 0) {
            // Get the current year from the CII alignment data
            const currentYear = Math.max(...vesselCiiAlignmentData.map((data) => data.year));

            const currentYearData = vesselLocationChartData.chartDataItemList.find((data) => data.year === currentYear);

            const currentVesselCiiAlignmentData = vesselCiiAlignmentData.find((data) => data.year === currentYear);

            const ppTrajectoryMinimum = parseFloat(currentYearData!.ppTrajectoryMinimum.toFixed(1));
            const ppTrajectoryStriving = parseFloat(currentYearData!.ppTrajectoryStriving.toFixed(1));

            const alignmentMin = currentVesselCiiAlignmentData?.currentAerWtw && currentVesselCiiAlignmentData?.currentAerWtw.toString() !== "-" && (100 * (currentVesselCiiAlignmentData?.currentAerWtw - ppTrajectoryMinimum) / ppTrajectoryMinimum);
            const alignmentStriving = currentVesselCiiAlignmentData?.currentAerWtw && currentVesselCiiAlignmentData?.currentAerWtw.toString() !== "-" && (100 * (currentVesselCiiAlignmentData?.currentAerWtw - ppTrajectoryStriving) / ppTrajectoryStriving);

            // Add the alignment values to the current year data
            const currentYearDataWithAlignment = {
                ...currentYearData,
                alignmentMin: alignmentMin,
                alignmentStriving: alignmentStriving,
            } as IAerEstimator;
            return currentYearDataWithAlignment;
        }
        return null;
    }
);

export const selectEnvironmentalCredentialsMaxYear = createSelector(
    selectgreenSlice,
    (greenSlice) => {
        const environmentalCredentials = greenSlice.msiEnvironmentalCredentialsData?.msiEnvironmentalCredentialsDatas;
        if (environmentalCredentials && environmentalCredentials.length > 0) {
            const years = environmentalCredentials.map((data) => data.aerEstimators).flat().map((est) => Number(est.year));
            const maxYear = Math.max(...years);
            return maxYear;
        }
        return 0;
    }
);

export const selectSummaryAvgClimateAlignment = createSelector(
    selectgreenSlice, searchSlice,
    (greenSlice, searchSlice) => {

        const environmentalCredentialsNew = greenSlice.greenSummaryData?.greenSummaryData;
        const totalImoCount = searchSlice.selectedVesselsList && searchSlice.selectedVesselsList.length || 0;

        if (totalImoCount > 0 && environmentalCredentialsNew && environmentalCredentialsNew.length > 0 && Array.isArray(environmentalCredentialsNew)) {
            const avgClimateAlignment = environmentalCredentialsNew.map((data) => data.avgClimateAlignment).reduce((acc, current) => acc + current, 0) / environmentalCredentialsNew.length;
            const calculatedImoCount = environmentalCredentialsNew.map((data) => data.shipCount ?? 0).reduce((acc, current) => acc + current, 0);
            const coveragePercentage = totalImoCount > 0 ? (calculatedImoCount / totalImoCount) * 100 : 0;

            return {
                avgClimateAlignment,
                totalImoCount,
                calculatedImoCount,
                coveragePercentage,
            };
        }
        return {
            avgClimateAlignment: 0,
            totalImoCount,
            calculatedImoCount: 0,
            coveragePercentage: 0,
        };
    }
);